import React from 'react'
import { axiosInstance, useAxios } from '../hooks/useAxios'
import { getUserLanguagesList } from '../api/getUserLanguagesList'
import Card from '../components/Card'
import Skeleton from 'react-loading-skeleton'
import { useForm } from 'react-hook-form'
import Btn from '../components/Btn'
import { getReference } from '../api/getReference'
import { toast } from 'react-toastify'
import SelectForForm from '../components/SelectForForm'
import { ILanguageItem } from '../server/user/info/languages/list/get'
import Chip from '../components/Chip'
import BtnIcon from '../components/BtnIcon'
import { ReactSVG } from 'react-svg'
import { deleteUserLanguage } from '../api/deleteUserLanguage'
import { addUserLanguage } from '../api/addUserLanguage'

function ProfileEditLanguagePage(): React.ReactElement {
  const [loading, setLoading] = React.useState<boolean>(true)
  const { error, fire } = useAxios()
  const { response: languages, fire: languagesFire } = useAxios()
  const { response: levels, fire: levelsFire } = useAxios()
  const { fire: submit, loading: submitLoading } = useAxios()
  const {
    control,
    reset,
    formState: { errors },
    handleSubmit
  } = useForm()
  const [items, setItems] = React.useState<Array<ILanguageItem>>([])

  React.useEffect(() => {
    setLoading(true)

    const itemsPromise = fire(getUserLanguagesList()).then(({ response }) => {
      setItems((response?.data as Array<ILanguageItem>) ?? [])
    })

    const levelsPromise = levelsFire(
      getReference('languages-levels', { all: true })
    ).catch(() => {})

    const languagesPromise = languagesFire(
      getReference('languages', { all: true })
    ).catch(() => {})

    Promise.all([itemsPromise, levelsPromise, languagesPromise]).finally(() => {
      setLoading(false)
    })
  }, [])

  const deleteHandler = async (id: string) => {
    try {
      await axiosInstance(deleteUserLanguage(id))

      setItems(items.filter((item) => item.id !== id))
      toast.success('Язык успешно удален')
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  const onSubmit = async (data: any) => {
    const { error, response } = await submit(addUserLanguage(data))

    if (error) {
      toast.error(error.message)
    } else {
      toast.success('Язык успешно добавлен')
      reset()

      setItems([...items, response?.data as ILanguageItem])
    }
  }

  return loading || !error ? (
    <Card>
      <header className={'card-head d-block text-h3'}>
        {loading ? <Skeleton></Skeleton> : 'Знание языков'}
      </header>
      <div className={'card-content'}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={'row row-gap-20 row-mobile-column u-flexAlignItemsCenter'}
        >
          <div className={'row row-gap-20 row-mobile-column u-flexGrow1'}>
            {loading ? (
              <>
                <div className={'u-flexGrow1'}>
                  <Skeleton height={'5rem'}></Skeleton>
                </div>
                <div className={'u-flexGrow1'}>
                  <Skeleton height={'5rem'}></Skeleton>
                </div>
              </>
            ) : (
              <>
                <SelectForForm
                  fullValue
                  control={control}
                  options={languages?.data ?? []}
                  className={'u-flexGrow1'}
                  name={'name'}
                  hideDetails
                  label={'Название языка'}
                  rules={{ required: true }}
                  required
                  hasError={!!errors.name}
                  searchable
                ></SelectForForm>
                <SelectForForm
                  fullValue
                  options={levels?.data ?? []}
                  control={control}
                  className={'u-flexGrow1'}
                  name={'level'}
                  hideDetails
                  label={'Уровень'}
                  rules={{ required: true }}
                  hasError={!!errors.level}
                  required
                ></SelectForForm>
              </>
            )}
          </div>
          <div>
            {loading ? (
              <div style={{ width: '10rem' }}>
                <Skeleton height={'4rem'}></Skeleton>
              </div>
            ) : (
              <Btn loading={loading} disabled={submitLoading} type={'submit'}>
                Добавить
              </Btn>
            )}
          </div>
        </form>

        {items.length || loading ? (
          <div className={'row row-gap-8 mt-md-8 mt-sm-6'}>
            {loading ? (
              <>
                {Array.from({ length: 2 }).map((_, index) => {
                  return (
                    <div key={index} style={{ width: '12rem' }}>
                      <Skeleton height={'3.2rem'}></Skeleton>
                    </div>
                  )
                })}
              </>
            ) : (
              <>
                {items.map((item: ILanguageItem) => {
                  return (
                    <Chip
                      className={
                        'text-body-small u-flex u-flexAlignItemsCenter'
                      }
                      key={item.id}
                    >
                      <>
                        <span>
                          {item.name.label}{' '}
                          <span className={'text-secondary-color'}>
                            {item.level.label}
                          </span>
                        </span>
                        <BtnIcon
                          onClick={() => deleteHandler(item.id)}
                          className={'ml-md-2 ml-sm-1'}
                        >
                          <ReactSVG
                            src={'/i/svg/x.svg'}
                            wrapper={'span'}
                            className={'svg-icon'}
                          ></ReactSVG>
                        </BtnIcon>
                      </>
                    </Chip>
                  )
                })}
              </>
            )}
          </div>
        ) : null}
      </div>
    </Card>
  ) : (
    error.message
  )
}

export default ProfileEditLanguagePage
